import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Tooltip, Checkbox, ListItemText, Popover, ListItemIcon } from '@mui/material';
import { Delete as DeleteIcon, ArrowDropDown as DropdownIcon } from '@mui/icons-material';
import { List, ListItem } from '@mui/material';
import { IconButton } from '@mui/material';
import { Button } from 'components/Button';
import MultiSelectDialog from 'components/MultiSelectDialog';
import { INTENTION_TYPE } from 'components/MultiSelectDialog/MultiSelectDialog';
import InfoIcon from '@mui/icons-material/ErrorOutline';
import { injectIntl } from 'react-intl';

class Filter extends PureComponent {
  static propTypes = {
    intl: PropTypes.object,
    filter: PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ).isRequired,
    }).isRequired,
    selectedOptions: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    onDelete: PropTypes.func.isRequired,
    onOptionsChange: PropTypes.func.isRequired,
    alwaysShowModal: PropTypes.bool,
    validationMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  };

  static defaultProps = {
    alwaysShowModal: false,
    validationMessage: '',
  };

  constructor(props) {
    super(props);
    const { selectedOptions } = props;
    const multiOptions = this.getMultiOptions();
    this.state = {
      showOptions: false,
      anchorEl: null,
      searchText: '',
      multiSelectedOptions: selectedOptions.map(selectedId =>
        multiOptions.find(({ id }) => selectedId === id),
      ),
    };
  }

  // Derived fresh from props on every read so a refetched filter.options
  // list can never go stale against a frozen constructor-time snapshot.
  getMultiOptions = () =>
    (this.props.filter.options || []).map(({ label, value }) => ({
      label,
      id: value,
    }));

  handleToggleOptions = event => {
    const opening = !this.state.showOptions;
    const multiOptions = this.getMultiOptions();
    this.setState({
      showOptions: opening,
      anchorEl: opening ? event.currentTarget : null,
      ...(opening
        ? {
            multiSelectedOptions: (this.props.selectedOptions || [])
              .map(id => multiOptions.find(o => o.id === id))
              .filter(Boolean),
          }
        : {}),
    });
  };

  handleClose = () => {
    this.setState({
      showOptions: false,
      anchorEl: null,
    });
  };

  handleOptionChange = option => {
    const { filter, selectedOptions, onOptionsChange } = this.props;
    const { value } = option;
    const updatedOptions = selectedOptions.includes(value)
      ? selectedOptions.filter(optVal => optVal !== value)
      : [...selectedOptions, value];

    onOptionsChange(filter.name, updatedOptions);
  };

  handleMultiChange = selectedOptions => {
    this.setState({ multiSelectedOptions: selectedOptions });
  };
  searchMultiOptions = text => {
    this.setState({ searchText: text.toLowerCase() });
  };
  handleMultiSubmit = () => {
    const { filter, onOptionsChange } = this.props;
    const { multiSelectedOptions } = this.state;
    onOptionsChange(filter.name, multiSelectedOptions.map(o => o.id));
    this.handleClose();
  };
  renderChips = () => {
    const { selectedOptions } = this.props;
    // const chipsToShow = 3;
    if (!selectedOptions /* || !selectedOptions.length */) return null;
    // const remainingCount = selectedOptions.length - chipsToShow;
    return (
      <Chip label={`${selectedOptions.length}`} />
      // <Fragment>
      //   {selectedOptions
      //     .slice(0, chipsToShow)
      //     .map((option, index) => <Chip key={index} label={option.label} />)}
      //   {remainingCount > 0 && <Chip label={`${remainingCount} more...`} />}
      // </Fragment>
    );
  };

  renderMultiOption = option => (
    <Box sx={{ display: 'flex', alignItems: 'center' }} className="pointer">
      <span>{option.label}</span>
    </Box>
  );

  renderOptions = () => {
    const { filter, alwaysShowModal, selectedOptions } = this.props;
    const { showOptions, anchorEl, multiSelectedOptions, searchText } = this.state;
    const multiOptions = this.getMultiOptions();

    if (filter.options.length > 5 || alwaysShowModal) {
      const availableOptions =
        (searchText && multiOptions.filter(o => o.label.toLowerCase().includes(searchText))) ||
        multiOptions;
      return (
        <MultiSelectDialog
          intention={INTENTION_TYPE.MODIFY}
          selectedOptions={multiSelectedOptions}
          options={availableOptions}
          onClose={this.handleClose}
          onChange={this.handleMultiChange}
          onSearch={this.searchMultiOptions}
          onSubmit={this.handleMultiSubmit}
          optionRenderer={this.renderMultiOption}
          titleLabelId="notifications.filter.multi-select.title"
          noResultsLabelId="notifications.filter.multi-select.search.no-results"
          searchingLabelId="notifications.filter.multi-select.search.searching"
          placeholderLabelId="notifications.filter.multi-select.search.placeholder"
          noSelectionLabelId="notifications.filter.multi-select.no-selections"
          selectedHeadingLabelId="notifications.filter.multi-select.heading.selected"
          removeActionTooltipLabelId="notifications.filter.multi-select.remove.tooltip"
          submitActionLabelId="notifications.filter.multi-select.button.submit"
        />
      );
    }
    return (
      <Popover
        open={showOptions}
        anchorEl={anchorEl}
        onClose={this.handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <List disablePadding>
          {filter.options.map((option, index) => (
            <ListItem key={index} button divider onClick={() => this.handleOptionChange(option)}>
              <ListItemIcon>
                <Checkbox color="primary" checked={selectedOptions.includes(option.value)} />
              </ListItemIcon>
              <ListItemText primary={option.label} />
            </ListItem>
          ))}
        </List>
      </Popover>
    );
  };

  render() {
    const { filter, onDelete, validationMessage, intl } = this.props;
    const isOverviewFilter = filter.name === 'FROM_OVERVIEW';

    return (
      <ListItem disableGutters divider sx={{ display: 'block' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isOverviewFilter ? (
            <IconButton disabled sx={{ mr: 0.5 }} style={{ height: '32px', width: '32px' }}>
              <InfoIcon color="primary" />
            </IconButton>
          ) : (
            <IconButton
              onClick={() => onDelete(filter.name)}
              style={{ height: '32px', width: '32px' }}
              sx={{ mr: 0.5 }}
            >
              <Tooltip title={intl.formatMessage({ id: 'notifications.filters.tooltip.remove' })}>
                <DeleteIcon color="error" />
              </Tooltip>
            </IconButton>
          )}
          <Tooltip
            title={intl.formatMessage(
              { id: 'notifications.filters.tooltip.description' },
              { description: filter.description },
            )}
          >
            <Box component="span" sx={{ flexGrow: 1 }} className="default-cursor">
              {filter.label}
            </Box>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{this.renderChips()}</Box>
          <Tooltip title={filter.description}>
            {/* <IconButton */}
            {/*   onClick={this.handleToggleOptions} */}
            {/*   style={{ height: '32px', width: '32px' }} */}
            {/*   className="" */}
            {/* > */}
            {/*   {filter.options.length > 5 || this.props.alwaysShowModal ? ( */}
            {/*     <MoreVertIcon /> */}
            {/*   ) : ( */}
            {/*     <DropdownIcon /> */}
            {/*   )} */}
            {/* </IconButton> */}
            <Button
              onClick={this.handleToggleOptions}
              // style={{ height: '32px', width: '32px' }}
              labelId="notifications.filters.button.options"
              variant="outlined"
              color="primary"
              size="small"
              sx={{ ml: 0.5, py: 0, pl: 2 }}
              iconRight={
                filter.options.length > 5 || this.props.alwaysShowModal ? (
                  <DropdownIcon />
                ) : (
                  <DropdownIcon />
                )
              }
            />
          </Tooltip>
          {this.state.showOptions && this.renderOptions()}
        </Box>
        {validationMessage && (
          <div>
            <Box style={{ color: 'red', fontSize: '12px' }} sx={{ mt: 0.5 }}>
              {validationMessage}
            </Box>
          </div>
        )}
      </ListItem>
    );
  }
}

export default injectIntl(Filter);
